import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { HAL, useIndeling } from './indeling'
import { DAKLICHT_Y, daklichtRijen } from './daglicht'
import { useLicht, useOppervlak } from '../scene/store'
import { useOppervlakken } from './useOppervlakken'

/** Eén sandwichpaneel is een meter breed; de map bevat er zes. */
const PANEEL = 6

/** De binnenmaat van de hal: tot hier lopen dak en wanden. */
const HALF_X = HAL.breedte * 0.9
const HALF_Z = HAL.diepte * 0.9

/**
 * Een horizontaal vlak met wereld-UV's: `u = x / tegel`. Zo loopt het
 * paneelpatroon naadloos door over losse stukken dak, en passen de naden van
 * de panelen op elkaar in plaats van per stuk opnieuw te beginnen.
 */
function dakstuk(x0: number, x1: number, z0: number, z1: number, y: number) {
  const g = new THREE.PlaneGeometry(x1 - x0, z1 - z0)
  g.rotateX(Math.PI / 2) // normaal naar beneden: we zien de onderkant
  g.translate((x0 + x1) / 2, y, (z0 + z1) / 2)
  const pos = g.attributes.position as THREE.BufferAttribute
  const uv = g.attributes.uv as THREE.BufferAttribute
  for (let i = 0; i < uv.count; i++) uv.setXY(i, pos.getX(i) / PANEEL, pos.getZ(i) / PANEEL)
  return g
}

/** Verticaal vlak met wereld-UV's langs de wand. */
function wandstuk(lengte: number, hoogte: number, tegel: number) {
  const g = new THREE.PlaneGeometry(lengte, hoogte)
  const uv = g.attributes.uv as THREE.BufferAttribute
  for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) * lengte) / tegel, (uv.getY(i) * hoogte) / tegel)
  return g
}

/** Lichtband: hoogte van de doorschijnende strook bovenin de gevels. */
const BAND_ONDER = 6.9
const BAND_BOVEN = 8.6

/**
 * Het dak, de daklichtstraten en de lichtband in de gevels.
 *
 * ## Het dak heeft gaten
 *
 * Tot nu toe was het plafond één vlak en kwam het "zonlicht" schuin door dat
 * vlak heen, over de hele hal tegelijk. Dat leest als buitenlicht: overal
 * dezelfde harde schaduw, onder elke stelling dezelfde schaduwrichting, en
 * geen enkele plek die lichter is dan een andere. Een hal is juist een doos
 * waar het licht op een paar plekken binnenkomt.
 *
 * Het dak is nu opgebouwd uit stukken rond de openingen van de daklichten, en
 * het werpt schaduw. De zon (`Licht.tsx`) komt dus alleen nog door de
 * openingen naar binnen en tekent vlekken op de vloer, doorsneden door de
 * schaduw van gordingen en spanten. `Lichtschachten.ts` gebruikt diezelfde
 * schaduwkaart voor het licht in de lucht, dus vlek en schacht horen bij
 * elkaar zonder dat iemand ze op elkaar hoeft af te stemmen.
 */
export function Dak() {
  const ind = useIndeling()
  const licht = useLicht()
  const opp = useOppervlak()
  const set = useOppervlakken()

  const rijen = useMemo(() => (licht.daklichten ? daklichtRijen(ind) : []), [ind, licht.daklichten])

  const dak = useMemo(() => {
    const y = HAL.hoogte
    const stukken: THREE.BufferGeometry[] = []
    // De rijen op z gesorteerd; tussen twee rijen een volle strook dak, binnen
    // een rij stukken tussen de openingen.
    const gesorteerd = [...rijen].sort((a, b) => a.z - b.z)
    let z = -HALF_Z
    for (const rij of gesorteerd) {
      const b = rij.vakken[0].breedte / 2
      if (rij.z - b > z) stukken.push(dakstuk(-HALF_X, HALF_X, z, rij.z - b, y))
      let x = -HALF_X
      for (const v of rij.vakken) {
        const a = v.x - v.lengte / 2
        if (a > x) stukken.push(dakstuk(x, a, rij.z - b, rij.z + b, y))
        x = v.x + v.lengte / 2
      }
      stukken.push(dakstuk(x, HALF_X, rij.z - b, rij.z + b, y))
      z = rij.z + b
    }
    stukken.push(dakstuk(-HALF_X, HALF_X, z, HALF_Z, y))
    const g = mergeGeometries(stukken, false)!
    stukken.forEach((s) => s.dispose())
    return g
  }, [rijen])

  const wanden = useMemo(
    () => ({ z: wandstuk(HALF_X * 2, HAL.hoogte, PANEEL), x: wandstuk(HALF_Z * 2, HAL.hoogte, PANEEL) }),
    [],
  )

  useEffect(() => () => dak.dispose(), [dak])
  useEffect(() => () => { wanden.z.dispose(); wanden.x.dispose() }, [wanden])

  // Daklichtvlakken, opstanden en de roeden van de lichtband: drie instanced
  // meshes, ongeacht het aantal.
  const vlakken = useRef<THREE.InstancedMesh>(null)
  const kaders = useRef<THREE.InstancedMesh>(null)
  const roeden = useRef<THREE.InstancedMesh>(null)

  // Van boven gezien zijn opstanden en roeden losse lijsten boven de hal. Ze
  // gaan mee weg met de plafondconstructie (zie `Plafond.tsx`).
  const bovenop = useRef<THREE.Group>(null)
  useFrame(({ camera }) => {
    if (bovenop.current) bovenop.current.visible = camera.position.y < HAL.hoogte - 0.2
  })

  const opbouw = useMemo(() => {
    const d = new THREE.Object3D()
    const vlak: THREE.Matrix4[] = []
    const kader: THREE.Matrix4[] = []
    const zet = (a: THREE.Matrix4[]) => { d.updateMatrix(); a.push(d.matrix.clone()) }

    for (const rij of rijen) {
      for (const v of rij.vakken) {
        d.position.set(v.x, DAKLICHT_Y + 0.35, v.z)
        d.rotation.set(Math.PI / 2, 0, 0)
        d.scale.set(v.lengte, v.breedte, 1)
        zet(vlak)
        // Opstand: vier dunne wanden rond de opening, zoals een lichtkoepel
        // op een opstand staat. Van onderen zie je die als een donkere lijst.
        for (const [dx, dz, sx, sz] of [
          [0, -v.breedte / 2, v.lengte + 0.12, 0.06],
          [0, v.breedte / 2, v.lengte + 0.12, 0.06],
          [-v.lengte / 2, 0, 0.06, v.breedte],
          [v.lengte / 2, 0, 0.06, v.breedte],
        ] as const) {
          d.position.set(v.x + dx, HAL.hoogte + 0.17, v.z + dz)
          d.rotation.set(0, 0, 0)
          d.scale.set(sx, 0.36, sz)
          zet(kader)
        }
      }
    }

    // Roeden in de lichtband, om de 1,25 m, op alle vier de gevels.
    const roed: THREE.Matrix4[] = []
    if (licht.lichtband) {
      const hoogte = BAND_BOVEN - BAND_ONDER + 0.1
      const midden = (BAND_BOVEN + BAND_ONDER) / 2
      for (let x = -HALF_X; x <= HALF_X; x += 1.25) {
        for (const z of [-HALF_Z + 0.06, HALF_Z - 0.06]) {
          d.position.set(x, midden, z); d.rotation.set(0, 0, 0); d.scale.set(0.07, hoogte, 0.08)
          zet(roed)
        }
      }
      for (let z = -HALF_Z; z <= HALF_Z; z += 1.25) {
        for (const x of [-HALF_X + 0.06, HALF_X - 0.06]) {
          d.position.set(x, midden, z); d.rotation.set(0, 0, 0); d.scale.set(0.08, hoogte, 0.07)
          zet(roed)
        }
      }
      // Kalf en dorpel: de horizontale regels boven en onder de band.
      for (const y of [BAND_ONDER - 0.05, BAND_BOVEN + 0.05, midden]) {
        for (const z of [-HALF_Z + 0.07, HALF_Z - 0.07]) {
          d.position.set(0, y, z); d.scale.set(HALF_X * 2, y === midden ? 0.04 : 0.12, 0.1)
          zet(roed)
        }
        for (const x of [-HALF_X + 0.07, HALF_X - 0.07]) {
          d.position.set(x, y, 0); d.scale.set(0.1, y === midden ? 0.04 : 0.12, HALF_Z * 2)
          zet(roed)
        }
      }
    }

    return { vlak, kader, roed }
  }, [rijen, licht.lichtband])

  useLayoutEffect(() => {
    for (const [mesh, m] of [
      [vlakken.current, opbouw.vlak],
      [kaders.current, opbouw.kader],
      [roeden.current, opbouw.roed],
    ] as const) {
      if (!mesh) continue
      m.forEach((mat, i) => mesh.setMatrixAt(i, mat))
      mesh.count = m.length
      mesh.instanceMatrix.needsUpdate = true
      mesh.computeBoundingSphere()
    }
  }, [opbouw])

  const paneel = (sterkte = 1) =>
    set
      ? {
          map: set.paneel.map,
          roughnessMap: set.paneel.roughnessMap,
          normalMap: set.paneel.normalMap,
          normalScale: new THREE.Vector2(opp.relief * sterkte, opp.relief * sterkte),
        }
      : {}

  // Daglicht door polycarbonaat: licht koel-wit en véél feller dan binnen. Een
  // factor tien boven het armatuurlicht is nog bescheiden — buiten is het op
  // een bewolkte dag zo'n honderd keer lichter dan in een magazijn. Na de
  // tone mapping blijft hier een wit vlak met een zachte gloed van over, en
  // dat is precies wat een camera van een daklicht maakt.
  const dag = licht.daglicht
  const daklichtKleur = useMemo(
    () => new THREE.Color('#f2f5fc').multiplyScalar(12 * dag),
    [dag],
  )
  const bandKleur = useMemo(() => new THREE.Color('#dfe9f7').multiplyScalar(4.2 * dag), [dag])

  const bandHoogte = BAND_BOVEN - BAND_ONDER
  const bandMidden = (BAND_BOVEN + BAND_ONDER) / 2

  return (
    <group>
      {/* Dak, van onderen gezien. Werpt schaduw: dat is wat de zon tegenhoudt. */}
      <mesh geometry={dak} castShadow receiveShadow>
        <meshStandardMaterial color="#1a1d22" roughness={0.9} metalness={0.2} {...paneel(0.6)} />
      </mesh>

      <group ref={bovenop}>
        <instancedMesh ref={vlakken} args={[undefined, undefined, Math.max(1, opbouw.vlak.length)]} frustumCulled={false}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial color={daklichtKleur} toneMapped={false} />
        </instancedMesh>

        <instancedMesh ref={kaders} args={[undefined, undefined, Math.max(1, opbouw.kader.length)]} castShadow>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color="#2c3138" roughness={0.55} metalness={0.6} />
        </instancedMesh>

        <instancedMesh ref={roeden} args={[undefined, undefined, Math.max(1, opbouw.roed.length)]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color="#23272d" roughness={0.6} metalness={0.5} />
        </instancedMesh>
      </group>

      {/* Wanden: sandwichpanelen, met de lichtband erin. */}
      {([[-HALF_Z, 0], [HALF_Z, Math.PI]] as const).map(([z, ry], i) => (
        <mesh key={`z${i}`} position={[0, HAL.hoogte / 2, z]} rotation={[0, ry, 0]} receiveShadow geometry={wanden.z}>
          <meshStandardMaterial color="#1b1f25" roughness={0.9} {...paneel()} />
        </mesh>
      ))}
      {([[-HALF_X, Math.PI / 2], [HALF_X, -Math.PI / 2]] as const).map(([x, ry], i) => (
        <mesh key={`x${i}`} position={[x, HAL.hoogte / 2, 0]} rotation={[0, ry, 0]} receiveShadow geometry={wanden.x}>
          <meshStandardMaterial color="#1b1f25" roughness={0.9} {...paneel()} />
        </mesh>
      ))}

      {licht.lichtband && (
        <group>
          {([[-HALF_Z + 0.03, 0, HALF_X * 2], [HALF_Z - 0.03, Math.PI, HALF_X * 2]] as const).map(([z, ry, l], i) => (
            <mesh key={`bz${i}`} position={[0, bandMidden, z]} rotation={[0, ry, 0]}>
              <planeGeometry args={[l, bandHoogte]} />
              <meshBasicMaterial color={bandKleur} toneMapped={false} />
            </mesh>
          ))}
          {([[-HALF_X + 0.03, Math.PI / 2, HALF_Z * 2], [HALF_X - 0.03, -Math.PI / 2, HALF_Z * 2]] as const).map(([x, ry, l], i) => (
            <mesh key={`bx${i}`} position={[x, bandMidden, 0]} rotation={[0, ry, 0]}>
              <planeGeometry args={[l, bandHoogte]} />
              <meshBasicMaterial color={bandKleur} toneMapped={false} />
            </mesh>
          ))}
        </group>
      )}

    </group>
  )
}
